/**
 * Pricing Calculator Utility
 * Based on Oil Capacity Baseline Analysis - December 2025
 * Simple transparent pricing with baseline oil volumes + surcharges
 */

/**
 * BASELINE OIL CAPACITIES (Included in Base Price)
 * Calculated from 24,010 vehicle database analysis
 */
const BASELINES = {
  standard: 5.0,   // 52.7% of standard vehicles have exactly 5.0qt
  european: 5.0,   // European baseline: up to 5 quarts included
  truck: 6.0       // Average 6.07qt, rounded to 6.0qt
};

/**
 * BASE PACKAGE PRICES
 * Includes oil up to baseline, filter, labor, and supplies
 */
const BASE_PRICES = {
  standard: 125,
  european: 125,
  truck: 150,
  jump_start: 99
};

/**
 * OIL SURCHARGE RATE
 * Charge per quart over baseline (standard rounding: .5+ rounds up)
 */
const OIL_SURCHARGE_PER_QUART = 10;

/**
 * Calculate oil surcharge for capacity exceeding baseline
 * Uses standard rounding: 5.1-5.4 = 5qt (no charge), 5.5+ = 6qt (charge)
 * @param {number} oilCapacityQts - Vehicle's oil capacity in quarts
 * @param {number} baseline - Category baseline capacity
 * @returns {Object} {extraQuarts, surcharge}
 */
function calculateOilSurcharge(oilCapacityQts, baseline) {
  if (!oilCapacityQts || oilCapacityQts <= baseline) {
    return { extraQuarts: 0, surcharge: 0 };
  }

  const extraQuarts = oilCapacityQts - baseline;
  // Standard rounding: .5+ rounds up, below .5 rounds down
  const extraCharged = Math.round(extraQuarts);
  const surcharge = extraCharged * OIL_SURCHARGE_PER_QUART;

  return {
    extraQuarts: Math.round(extraQuarts * 10) / 10, // Display with 1 decimal
    extraCharged: extraCharged,
    surcharge: surcharge
  };
}

/**
 * Check if vehicle is electric (no oil service needed)
 * @param {string} engine - Engine description
 * @returns {boolean}
 */
function isElectric(engine) {
  if (!engine) return false;
  const engineLower = engine.toLowerCase();
  return engineLower.includes('electric') ||
         engineLower.includes('ev') ||
         engineLower === 'electric motor';
}

/**
 * Main pricing calculation function - SIMPLIFIED BASELINE MODEL
 * @param {Object} vehicle - Vehicle specification object from database
 * @returns {Object} Price breakdown
 */
export function calculateTotalPrice(vehicle) {
  // Jump Start: flat rate, no vehicle-based calculation
  const cat = vehicle.category || vehicle.brand_category || 'standard';
  if (cat === 'jump_start') {
    return {
      base_price: BASE_PRICES.jump_start,
      oil_surcharge: 0,
      final_price: BASE_PRICES.jump_start,
      baseline_capacity: 0,
      vehicle_capacity: 0,
      is_jump_start: true
    };
  }

  // Extract vehicle data (handle both nested specs and flat structure)
  const oilCapacityQts = vehicle.specs?.oil_capacity_qts || vehicle.oil_capacity_qts;
  const category = vehicle.category || vehicle.brand_category || 'standard';
  const engine = vehicle.engine || vehicle.engine_code || '';

  // Handle electric vehicles
  if (isElectric(engine)) {
    return {
      is_electric: true,
      base_price: 0,
      oil_surcharge: 0,
      final_price: 0,
      message: 'Electric vehicle - no oil service needed',
      baseline_capacity: 0,
      vehicle_capacity: 0
    };
  }

  // Get baseline and base price for category
  const baseline = BASELINES[category] || BASELINES.standard;
  const basePrice = BASE_PRICES[category] || BASE_PRICES.standard;

  // Handle missing oil capacity data
  if (!oilCapacityQts || oilCapacityQts === 0) {
    return {
      base_price: basePrice,
      oil_surcharge: 0,
      final_price: basePrice,
      baseline_capacity: baseline,
      vehicle_capacity: baseline,
      estimated: true,
      message: `Estimated oil: ${baseline} qt - may vary by engine. Please verify with dipstick.`
    };
  }

  // Calculate oil surcharge
  const surchargeInfo = calculateOilSurcharge(oilCapacityQts, baseline);

  // Final price
  const finalPrice = basePrice + surchargeInfo.surcharge;

  // Build result
  const result = {
    base_price: basePrice,
    baseline_capacity: baseline,
    vehicle_capacity: oilCapacityQts,
    extra_oil_quarts: surchargeInfo.extraQuarts,
    extra_oil_charged: surchargeInfo.extraCharged,
    oil_surcharge: surchargeInfo.surcharge,
    final_price: finalPrice,
    category: category
  };

  // Add warning for large capacity engines (>12 quarts)
  if (oilCapacityQts > 12) {
    result.warning = `Large capacity engine (${oilCapacityQts}qt) - surcharge applies: $${surchargeInfo.surcharge}`;
  }

  // Add note if surcharge applies
  if (surchargeInfo.surcharge > 0) {
    result.surcharge_note = `+${surchargeInfo.extraCharged} quart${surchargeInfo.extraCharged > 1 ? 's' : ''} over baseline`;
  }

  return result;
}

/**
 * Get package information for a category
 * @param {string} category - Vehicle category (standard/european/truck)
 * @returns {Object} Package details
 */
export function getPackageInfo(category) {
  const cat = category || 'standard';
  return {
    base_price: BASE_PRICES[cat],
    oil_quarts_included: BASELINES[cat],
    surcharge_per_extra_quart: OIL_SURCHARGE_PER_QUART,
    includes: [
      `Up to ${BASELINES[cat]} quarts full synthetic oil`,
      'Premium oil filter',
      'Professional installation',
      'Multi-point inspection',
      'Disposal fees included'
    ]
  };
}

/**
 * Format price for display
 * @param {number} amount - Price amount
 * @returns {string} Formatted price string
 */
export function formatPrice(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Get pricing summary text for display
 * @param {Object} priceData - Result from calculateTotalPrice
 * @returns {string} Human-readable pricing summary
 */
export function getPricingSummary(priceData) {
  if (priceData.is_electric) {
    return priceData.message;
  }

  if (priceData.estimated) {
    return `${formatPrice(priceData.final_price)} - ${priceData.message}`;
  }

  let summary = `${formatPrice(priceData.final_price)}`;

  if (priceData.oil_surcharge > 0) {
    summary += ` (includes ${formatPrice(priceData.oil_surcharge)} oil surcharge)`;
  }

  if (priceData.warning) {
    summary += ` - ${priceData.warning}`;
  }

  return summary;
}

/**
 * Export baseline constants for external use
 */
export const PRICING_CONSTANTS = {
  BASELINES,
  BASE_PRICES,
  OIL_SURCHARGE_PER_QUART
};

// Default export
// eslint-disable-next-line import/no-anonymous-default-export
export default {
  calculateTotalPrice,
  getPackageInfo,
  formatPrice,
  getPricingSummary,
  PRICING_CONSTANTS
};
